<template>
    <UForm ref="form" :schema="schema" :state="state" :validate-on="['submit']" class="-m-4 sm:-m-6 lg:-mx-8 h-[calc(100vh-24px)] flex flex-col" @submit="onSubmit">
        <div class="shrink-0 px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-4 bg-gray-50">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <UButton :to="`/field-posts/${postId}`" color="gray" variant="ghost" icon="i-ph-arrow-left-bold" />
                    <h1 class="text-2xl font-semibold text-gray-700">New Intervention</h1>
                </div>
                <div class="flex items-center space-x-2">
                    <UButton :to="`/field-posts/${postId}`" size="lg" color="white">Cancel</UButton>
                    <UButton type="submit" size="lg" color="black" :loading="creating">Start Intervention</UButton>
                </div>
            </div>
        </div>

        <div class="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 pt-2 pb-12">
            <div class="flex gap-6 max-w-7xl mx-auto items-start">
                <div class="flex-1 space-y-6">
                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Patient</h4>
                            </div>
                            <div class="flex-1 space-y-4">
                                <UFormGroup v-if="!state.no_phone" name="phone" label="Phone" required>
                                    <div class="flex items-center space-x-2">
                                        <UInput
                                            v-model="state.phone"
                                            type="tel"
                                            inputmode="tel"
                                            autocomplete="off"
                                            placeholder="Eg. 70123456"
                                            icon="i-ph-phone-duotone"
                                            class="flex-1"
                                            @keydown.enter.prevent="lookup()"
                                        />
                                        <UButton color="white" label="Look Up" :loading="looking" @click="lookup()" />
                                    </div>
                                </UFormGroup>
                                <UCheckbox v-model="state.no_phone" label="Patient has no phone" @update:model-value="onNoPhoneChange" />

                                <div v-if="patient" class="grid grid-cols-2 gap-4 rounded-md border border-gray-200 px-4 py-3">
                                    <Field label="Name">{{ [patient.first_name, patient.last_name].filter(Boolean).join(' ') }}</Field>
                                    <Field label="Date of Birth">
                                        <template v-if="patient.birth_date">
                                            {{ dayjs(patient.birth_date).format('MMM D, YYYY') }}
                                            <span class="text-gray-500">({{ age(patient.birth_date) }})</span>
                                        </template>
                                    </Field>
                                    <Field label="Gender">
                                        <span v-if="patient.gender" class="capitalize">{{ patient.gender }}</span>
                                    </Field>
                                    <Field label="Allergies">
                                        <template v-if="patient.allergies">{{ patient.allergies }}</template>
                                    </Field>
                                </div>

                                <div v-else-if="looked" class="grid grid-cols-2 gap-4">
                                    <UFormGroup name="first_name" label="First Name" required>
                                        <UInput v-model="state.first_name" />
                                    </UFormGroup>
                                    <UFormGroup name="last_name" label="Last Name" required>
                                        <UInput v-model="state.last_name" />
                                    </UFormGroup>
                                    <UFormGroup name="birth_date" label="Date of Birth">
                                        <UInput v-model="state.birth_date" type="date" />
                                    </UFormGroup>
                                    <UFormGroup name="gender" label="Gender">
                                        <USelectMenu v-model="state.gender" :options="GENDER_OPTIONS" value-attribute="value" option-attribute="label" placeholder="Select gender..." />
                                    </UFormGroup>
                                    <UFormGroup name="blood_type" label="Blood Type">
                                        <USelectMenu v-model="state.blood_type" :options="BLOOD_TYPES" placeholder="Select blood type..." />
                                    </UFormGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Intervention</h4>
                            </div>
                            <div class="flex-1 grid grid-cols-2 gap-4">
                                <UFormGroup name="area" label="Area">
                                    <USelectMenu v-model="state.area" :options="post?.areas ?? []" placeholder="Select area..." />
                                </UFormGroup>
                                <UFormGroup name="reason_id" label="Reason">
                                    <USelectMenu v-model="state.reason_id" :options="reasons" value-attribute="id" option-attribute="label" placeholder="Select reason..." />
                                </UFormGroup>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </UForm>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { z } from 'zod'
import { OptionNamespace, type Option } from '~/types/option'
import { BLOOD_TYPES, FIELD_POST_WRITE_ROLES, GENDER_OPTIONS, type FieldPost, type FieldPostPatient } from '~/types/fieldPost'

definePageMeta({ layout: 'default', middleware: 'auth', roles: FIELD_POST_WRITE_ROLES })
useHead({ title: 'New Intervention' })

const route = useRoute()
const client = useSupabaseClient()
const toast = useToast()
const { age } = useNumber()

const postId = route.params.id as string
const post = ref<FieldPost>()
const reasons = ref<Option[]>([])

const form = ref()
const looking = ref(false)
const looked = ref(false)
const creating = ref(false)
const patient = ref<FieldPostPatient | null>(null)

const state = reactive({
    phone: '',
    no_phone: false,
    first_name: '',
    last_name: '',
    birth_date: '',
    gender: null as 'male' | 'female' | null,
    blood_type: null as string | null,
    area: null as string | null,
    reason_id: null as string | null,
})

const schema = z.object({
    phone: z.string(),
    no_phone: z.boolean(),
    first_name: z.string(),
    last_name: z.string(),
}).superRefine((data, ctx) => {
    if (!data.no_phone && !data.phone.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message: 'Enter a phone number or mark the patient as having no phone.' })
        return
    }
    if (!looked.value) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message: 'Look up the patient first.' })
        return
    }
    if (patient.value) return
    if (!data.first_name.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['first_name'], message: 'First name is required.' })
    if (!data.last_name.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['last_name'], message: 'Last name is required.' })
})

watch(() => state.phone, () => {
    if (state.no_phone) return
    looked.value = false
    patient.value = null
    form.value?.clear('phone')
})

onMounted(async () => {
    const [postRes, reasonsRes] = await Promise.all([
        client.from('field_posts').select('*').eq('id', postId).single(),
        client.from('options').select('*').eq('namespace', OptionNamespace.interventionReason).eq('is_archived', false).order('label'),
    ])
    post.value = postRes.data as FieldPost
    reasons.value = (reasonsRes.data ?? []) as Option[]
})

function onNoPhoneChange(noPhone: boolean) {
    patient.value = null
    looked.value = noPhone
    form.value?.clear('phone')
}

async function lookup() {
    if (state.no_phone || !state.phone.trim()) return
    looking.value = true
    form.value?.clear('phone')
    try {
        const res = await $fetch<{ patient: FieldPostPatient | null }>('/api/field-posts/patients/lookup', {
            query: { post_id: postId, phone: state.phone },
        })
        patient.value = res.patient
        looked.value = true
    } catch (e: any) {
        if (e?.data?.message === 'INVALID_PHONE') {
            form.value?.setErrors([{ path: 'phone', message: 'Enter a valid phone number.' }])
        } else {
            toast.add({ title: 'Lookup failed, please try again.', color: 'red', icon: 'i-ph-x-circle-duotone' })
        }
    } finally {
        looking.value = false
    }
}

async function onSubmit() {
    creating.value = true
    try {
        const details = {
            first_name: state.first_name,
            last_name: state.last_name,
            birth_date: state.birth_date || null,
            gender: state.gender,
            blood_type: state.blood_type,
        }
        const body = {
            post_id: postId,
            area: state.area,
            reason_id: state.reason_id,
            ...(state.no_phone ? {} : { phone: state.phone }),
            ...(patient.value ? {} : details),
        }
        const data = await $fetch<{ id: string }>('/api/field-posts/interventions', { method: 'POST', body })

        await navigateTo(`/field-posts/${postId}/interventions/${data.id}`, { replace: true })
    } catch (e: any) {
        const code = e?.data?.message
        if (code === 'INVALID_PHONE') {
            form.value?.setErrors([{ path: 'phone', message: 'Enter a valid phone number.' }])
        } else if (code === 'NAME_REQUIRED') {
            form.value?.setErrors([
                { path: 'first_name', message: 'First name is required.' },
                { path: 'last_name', message: 'Last name is required.' },
            ])
        } else if (code === 'INVALID_AREA') {
            form.value?.setErrors([{ path: 'area', message: 'Select an area from this post.' }])
        } else {
            toast.add({ title: 'Could not start the intervention.', description: code || e?.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        }
    } finally {
        creating.value = false
    }
}
</script>
